import { parseGedFile, buildFamilyTree, getMostRelevantRoot, getAncestors, getDescendants, extractPhotoPath } from "./gedParser";

/**
 * Test utility to create a minimal GED file content for testing
 */
const createMinimalGedContent = () => `0 HEAD
1 SOUR Gramps
1 DATE 23 MAR 2025
1 GEDC
2 VERS 5.5.1
2 FORM LINEAGE-LINKED
1 CHAR UTF-8
0 @I0001@ INDI
1 NAME John /Doe/
2 TYPE birth
2 GIVN John
2 SURN Doe
1 SEX M
1 BIRT
2 DATE 1 JAN 1950
2 PLAC New York
1 OBJE @O0001@
1 FAMS @F0001@
0 @I0002@ INDI
1 NAME Jane /Doe/
2 TYPE birth
2 GIVN Jane
2 SURN Doe
1 SEX F
1 BIRT
2 DATE 2 JAN 1952
2 PLAC Boston
1 FAMS @F0001@
0 @I0003@ INDI
1 NAME Jack /Doe/
2 TYPE birth
2 GIVN Jack
2 SURN Doe
1 SEX M
1 BIRT
2 DATE 15 MAR 1975
2 PLAC Chicago
1 FAMC @F0001@
2 PEDI birth
0 @I0004@ INDI
1 NAME Jill /Doe/
2 TYPE birth
2 GIVN Jill
2 SURN Doe
1 SEX F
1 BIRT
2 DATE 20 MAY 1978
2 PLAC Boston
1 FAMC @F0001@
2 PEDI birth
0 @O0001@ OBJE
1 FILE photo_john.jpg
0 @F0001@ FAM
1 HUSB @I0001@
1 WIFE @I0002@
1 CHIL @I0003@
1 CHIL @I0004@
0 TRLR
`;

/**
 * Test runner - validates parser functionality
 */
const runAllTests = () => {
    console.log("[GED Parser Tests] Running comprehensive tests...");

    const gedContent = createMinimalGedContent();
    const { individuals, families } = parseGedFile(gedContent);

    let passedTests = 0;
    let failedTests = 0;

    // Test 1: parseGedFile identifies individuals and families
    try {
        if (Object.keys(individuals).length === 4 && Object.keys(families).length === 1) {
            console.log("✓ parseGedFile: Correctly identifies individuals and families");
            passedTests += 1;
        } else {
            console.log("✗ parseGedFile: Failed to identify correct counts");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ parseGedFile test error:", e.message);
        failedTests += 1;
    }

    // Test 2: parseGedFile extracts names
    try {
        if (individuals.I0001.fullName && individuals.I0001.givenName === "John" && individuals.I0001.surname === "Doe") {
            console.log("✓ parseGedFile: Correctly extracts name data");
            passedTests += 1;
        } else {
            console.log("✗ parseGedFile: Failed to extract name data");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ parseGedFile name extraction error:", e.message);
        failedTests += 1;
    }

    // Test 3: parseGedFile extracts birth data
    try {
        if (individuals.I0001.birthData && individuals.I0001.birthData.date === "1 JAN 1950" && individuals.I0001.birthData.place === "New York") {
            console.log("✓ parseGedFile: Correctly extracts birth data");
            passedTests += 1;
        } else {
            console.log("✗ parseGedFile: Failed to extract birth data");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ parseGedFile birth data error:", e.message);
        failedTests += 1;
    }

    // Test 4: parseGedFile extracts family relationships
    try {
        const family = families.F0001;
        if (family.husband === "I0001" && family.wife === "I0002" && family.children.length === 2) {
            console.log("✓ parseGedFile: Correctly extracts family relationships");
            passedTests += 1;
        } else {
            console.log("✗ parseGedFile: Failed to extract family data");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ parseGedFile family data error:", e.message);
        failedTests += 1;
    }

    // Test 5: buildFamilyTree creates parent-child relationships
    try {
        const people = buildFamilyTree(individuals, families);
        const jack = people.find((p) => p.id === "I0003");
        if (jack && jack.parents.length === 2) {
            console.log("✓ buildFamilyTree: Correctly creates parent-child relationships");
            passedTests += 1;
        } else {
            console.log("✗ buildFamilyTree: Failed to create relationships");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ buildFamilyTree error:", e.message);
        failedTests += 1;
    }

    // Test 6: buildFamilyTree creates spouse relationships
    try {
        const people = buildFamilyTree(individuals, families);
        const john = people.find((p) => p.id === "I0001");
        if (john && john.spouse && john.spouse.id === "I0002") {
            console.log("✓ buildFamilyTree: Correctly creates spouse relationships");
            passedTests += 1;
        } else {
            console.log("✗ buildFamilyTree: Failed to create spouse relationships");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ buildFamilyTree spouse error:", e.message);
        failedTests += 1;
    }

    // Test 7: getMostRelevantRoot selects root
    try {
        const people = buildFamilyTree(individuals, families);
        const root = getMostRelevantRoot(people);
        if (root && ["I0001", "I0002"].includes(root.id)) {
            console.log("✓ getMostRelevantRoot: Correctly selects root person");
            passedTests += 1;
        } else {
            console.log("✗ getMostRelevantRoot: Failed to select root");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ getMostRelevantRoot error:", e.message);
        failedTests += 1;
    }

    // Test 8: getAncestors returns correct ancestors
    try {
        const people = buildFamilyTree(individuals, families);
        const jack = people.find((p) => p.id === "I0003");
        const ancestors = getAncestors(jack);
        if (ancestors.size === 2) {
            console.log("✓ getAncestors: Correctly returns ancestors");
            passedTests += 1;
        } else {
            console.log("✗ getAncestors: Failed to return correct ancestors");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ getAncestors error:", e.message);
        failedTests += 1;
    }

    // Test 9: getDescendants returns correct descendants
    try {
        const people = buildFamilyTree(individuals, families);
        const john = people.find((p) => p.id === "I0001");
        const descendants = getDescendants(john);
        if (descendants.size === 2) {
            console.log("✓ getDescendants: Correctly returns descendants");
            passedTests += 1;
        } else {
            console.log("✗ getDescendants: Failed to return correct descendants");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ getDescendants error:", e.message);
        failedTests += 1;
    }

    // Test 10: parseGedFile extracts photo ID
    try {
        if (individuals.I0001.photoId === "O0001") {
            console.log("✓ parseGedFile: Correctly extracts photo ID");
            passedTests += 1;
        } else {
            console.log("✗ parseGedFile: Failed to extract photo ID");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ parseGedFile photo ID error:", e.message);
        failedTests += 1;
    }

    // Test 11: extractPhotoPath returns correct photo path
    try {
        const photoPath = extractPhotoPath(gedContent, "O0001");
        if (photoPath === "/familytreedata/photos/photo_john.jpg") {
            console.log("✓ extractPhotoPath: Correctly extracts photo path");
            passedTests += 1;
        } else {
            console.log("✗ extractPhotoPath: Failed to extract photo path");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ extractPhotoPath error:", e.message);
        failedTests += 1;
    }

    // Test 12: Birth date parsing handles multiple formats
    try {
        const formats = [
            { input: "1 JAN 1950", shouldMatch: true },
            { input: "15 MAR 1975", shouldMatch: true },
            { input: "1950", shouldMatch: true },
        ];

        let formatsPassed = true;
        formats.forEach((format) => {
            const dateMatch = format.input.match(/(\d+)\s+(\w+)\s+(\d+)/);
            const yearMatch = format.input.match(/(\d{4})/);
            if (format.shouldMatch && !dateMatch && !yearMatch) {
                formatsPassed = false;
            }
        });

        if (formatsPassed) {
            console.log("✓ Date parsing: Handles multiple date formats");
            passedTests += 1;
        } else {
            console.log("✗ Date parsing: Failed to handle date formats");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ Date parsing error:", e.message);
        failedTests += 1;
    }

    // Test 13: Individuals have correct birthData structure
    try {
        const people = buildFamilyTree(individuals, families);
        const john = people.find((p) => p.id === "I0001");
        if (john && john.birthData && john.birthData.date === "1 JAN 1950" && john.birthData.place === "New York") {
            console.log("✓ Birth data structure: Correctly parsed and stored");
            passedTests += 1;
        } else {
            console.log("✗ Birth data structure: Failed to parse correctly");
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ Birth data structure error:", e.message);
        failedTests += 1;
    }

    // Test 14: Layout positioning includes all people
    try {
        // Simulate layout calculation
        const people = buildFamilyTree(individuals, families);
        const rootPerson = people.find((p) => !p.parents || p.parents.length === 0);

        // Create a simple layout function for testing
        const testPositions = new Map();
        const positioned = new Set();

        const positionTestPerson = (person, x, y) => {
            if (positioned.has(person.id)) return;
            positioned.add(person.id);
            testPositions.set(person.id, { x, y });

            if (person.children && person.children.length > 0) {
                let currentX = x - (person.children.length * 280) / 2;
                person.children.forEach((child) => {
                    positionTestPerson(child, currentX, y + 280);
                    currentX += 280;
                });
            }
        };

        if (rootPerson) {
            positionTestPerson(rootPerson, 0, 0);
        }

        // Position remaining people
        people.forEach((person) => {
            if (!positioned.has(person.id)) {
                positionTestPerson(person, 0, 0);
            }
        });

        if (testPositions.size === people.length) {
            console.log("✓ Layout positioning: All people are positioned");
            passedTests += 1;
        } else {
            console.log(`✗ Layout positioning: Only ${testPositions.size} of ${people.length} people positioned`);
            failedTests += 1;
        }
    } catch (e) {
        console.log("✗ Layout positioning error:", e.message);
        failedTests += 1;
    }

    console.log(`[GED Parser Tests] Completed: ${passedTests} passed, ${failedTests} failed`);
};

export default runAllTests;
export { runAllTests };
